import ImageLoader from "@/design-system/image-loader";
import TelemetryButton from "@/design-system/telemetry-button";
import { getCdnImage } from "@/lib/cdn-helper";
import { browserStatics } from "@/src/shared/BrowserStatic";
import type { PropsWithStyleX, QueryXsMax } from "@/styles/tokens.stylex";
import * as stylex from "@stylexjs/stylex";

const xsMax: QueryXsMax = "@media (max-width: 759px)";

const StoreLinks = {
    AppleStore: "https://apps.apple.com/pl/app/goodie-gazetki-promocje/id1173816688",
    GooglePlay: "https://play.google.com/store/apps/details?id=pl.goodie.prd",
};

const getAppStoreUrl = (referrer: string): string =>
    `${StoreLinks.AppleStore}?pt=118456293&ct=${referrer}&mt=8`;
const getGooglePlayUrl = (referrer: string): string =>
    `${StoreLinks.GooglePlay}&referrer=utm_source%3Dgoodie%26utm_medium%3D${referrer}`;

export interface IMobileStores extends PropsWithStyleX {
    referrer: string;
    telemetryOrigin?: string;
}

export default function MobileStores({ referrer, styleX, telemetryOrigin }: IMobileStores) {
    const isEnglish = browserStatics.culture === "en-US";
    const browser = browserStatics.browserOs.toUpperCase();

    const isNotAndroid = browser !== "ANDROID";
    const isNotIOS = browser !== "IOS";

    return (
        <div {...stylex.props(styles.container)}>
            {isNotIOS && (
                <TelemetryButton
                    styleX={[styles.button, styleX]}
                    as="a"
                    href={getGooglePlayUrl(referrer)}
                    target="_blank"
                    size="M"
                    rel="noopener nofollow noreferrer"
                    eventName="common-appBtnClick"
                    param1="1"
                    param2={telemetryOrigin}
                    aria-label="Pobierz aplikację goodie ze sklepu Google Play"
                >
                    <ImageLoader
                        styleX={styles.images}
                        src={getCdnImage(
                            isEnglish
                                ? "L2dvb2RpZS1hc3NldHMvbW9iaWxlX3N0b3JlL2dvb2dsZV9wbGF5X2VuLnBuZw"
                                : "L2dvb2RpZS1hc3NldHMvbW9iaWxlX3N0b3JlL2dvb2dsZV9wbGF5LnBuZw0K",
                        )}
                        alt=""
                        loading="lazy"
                        aria-hidden="true"
                    />
                </TelemetryButton>
            )}
            {isNotAndroid && (
                <TelemetryButton
                    styleX={[styles.button, styleX]}
                    as="a"
                    href={getAppStoreUrl(referrer)}
                    target="_blank"
                    size="M"
                    rel="noopener nofollow noreferrer"
                    eventName="common-appBtnClick"
                    param1="2"
                    param2={telemetryOrigin}
                    aria-label="Pobierz aplikację goodie ze sklepu App Store"
                >
                    <ImageLoader
                        styleX={styles.images}
                        src={getCdnImage(
                            isEnglish
                                ? "L2dvb2RpZS1hc3NldHMvbW9iaWxlX3N0b3JlL2FwcF9zdG9yZV9lbi5wbmc"
                                : "L2dvb2RpZS1hc3NldHMvbW9iaWxlX3N0b3JlL2FwcF9zdG9yZS5wbmc",
                        )}
                        alt=""
                        loading="lazy"
                        aria-hidden="true"
                    />
                </TelemetryButton>
            )}
        </div>
    );
}

const styles = stylex.create({
    container: {
        alignItems: "center",
        display: "flex",
        flexWrap: "wrap",
        justifyContent: { default: "flex-start", [xsMax]: "center" },
        width: "100%",
    },
    images: {
        height: "100%",
    },
    qrCode: {
        flexShrink: 0,
        height: 80,
        width: 80,
    },
    button: {
        paddingBottom: 0,
        paddingLeft: "0.7rem",
        paddingRight: "0.7rem",
        paddingTop: 0,
    },
});
